"use client";

import type { LanguageCode } from "@/config/i18n";
import type { MovieId } from "@/types/library";
import { Tooltip } from "@/components/ui/Tooltip";
import { useMovieLibraryState } from "@/lib/hooks/useLibrary";
import { createTranslator } from "@/lib/i18n/translations";

import { BookmarkIcon, CheckIcon, StarIcon } from "./icons";

const pill =
  "flex h-6 items-center gap-1 rounded-md px-1.5 text-xs font-semibold shadow-sm backdrop-blur-sm";

/**
 * Small "in watchlist / watched / your rating" badges for a poster.
 * Absolutely positioned by the caller, so appearing never shifts layout.
 */
export function LibraryStatusBadges({
  movieId,
  lang,
}: {
  movieId: MovieId;
  lang: LanguageCode;
}) {
  const state = useMovieLibraryState(movieId);
  const t = createTranslator(lang);

  if (!state.inWatchlist && !state.watched && state.rating === null) {
    return null;
  }

  return (
    <ul
      aria-label={t("library.movieActions.label")}
      className="flex flex-col items-start gap-1"
      data-testid="library-badges"
    >
      {state.inWatchlist && (
        <li>
          <Tooltip content={t("library.badges.inWatchlist")}>
            <span className={`${pill} bg-primary/90 text-primary-foreground`}>
              <BookmarkIcon filled size={12} />
              <span className="sr-only">{t("library.badges.inWatchlist")}</span>
            </span>
          </Tooltip>
        </li>
      )}
      {state.watched && (
        <li>
          <Tooltip content={t("library.badges.watched")}>
            <span className={`${pill} bg-success/90 text-success-foreground`}>
              <CheckIcon size={12} />
              <span className="sr-only">{t("library.badges.watched")}</span>
            </span>
          </Tooltip>
        </li>
      )}
      {state.rating !== null && (
        <li>
          <Tooltip
            content={t("library.badges.yourRating", { rating: state.rating })}
          >
            <span className={`${pill} bg-warning/90 text-warning-foreground`}>
              <StarIcon filled size={12} />
              <span aria-hidden="true">{state.rating}</span>
              <span className="sr-only">
                {t("library.badges.yourRating", { rating: state.rating })}
              </span>
            </span>
          </Tooltip>
        </li>
      )}
    </ul>
  );
}
